import { Navigate, useNavigate, useOutlet } from 'react-router-dom';
import { useEffect } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { CircularProgress, Container } from '@mui/material';
import AuthenticatedRoute from '../AuthenticatedRoute';
import { DashboardLayout } from './DashboardLayout';
import {
  getAllActivePartners, getMemberConfig, getPaymentMethods,
} from '../../store/thunks/dashboardThunk';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getMemberInformation } from '../../store/thunks/onboardingThunk';
import {
  selectGetMemberInformationStatus,
  selectMemberInformation,
  selectMemberSession,
} from '../../store/slices/onboardingSlice';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { Logger } from '../../config/logging/logger';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { getDashboardEntryRoute } from '../../common/dashboardEntryRoute';
import {
  EverProEdgePublicSolutionOrgName,
  ServiceFusionSolutionOrgName,
  ServiceNationSolutionOrgName,
} from '../../common/constants';
import { ONBOARDING_BASE_ROUTE } from '../../routes/namedRoutes';

// These orgs don't go through Edge onboarding, so an unenrolled member stays on the dashboard
const orgsWithoutEdgeOnboarding = [
  ServiceNationSolutionOrgName,
  EverProEdgePublicSolutionOrgName,
  ServiceFusionSolutionOrgName,
];

export function DashboardHome() {
  const outlet = useOutlet();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const memberSession = useAppSelector(selectMemberSession);
  const { company } = useAppSelector(selectMemberInformation);
  const getMemberInformationStatus = useAppSelector(selectGetMemberInformationStatus);
  const { solutionOrgName } = useSolutionOrg();
  const dashboardEntryRoute = getDashboardEntryRoute(solutionOrgName);
  const { trackRequest, trackSessionStarted } = useCustomEventTracking();
  const logger = Logger.create('DashboardHome');

  // Make/centralize API resource calls at the top level (dashboard home)
  // Resources are saved in the store and passed to components via useAppSelectors
  useEffect(() => {
    // Track authenticated session started
    if (memberSession.memberId) {
      const visitedAt = Math.floor(Date.now() / 1000);
      trackSessionStarted({ visitedAt });
    }

    dispatch(getMemberInformation(
      {
        id: memberSession.memberId,
        trackRequest,
      },
    )).then(unwrapResult)
      .then((memberInformation) => {
        // Members who haven't finished Edge onboarding are sent back to it. Home Depot
        // enrollment is not checked here - it's offered at the end of onboarding and from
        // the partners page.
        if (!memberInformation?.edgeEnrollmentDate &&
          !orgsWithoutEdgeOnboarding.includes(solutionOrgName as string)) {
          navigate(ONBOARDING_BASE_ROUTE);
          return;
        }

        // Every active partner, as on the Partners page: partner access is cumulative and
        // the tier gating happens per card, so a tier-filtered request here would leave the
        // shared store missing partners the member is entitled to see (SN-996).
        dispatch(getAllActivePartners());
      })
      .catch((error) => {
        logger.error(Logger.format('Request failed', error));
      });

    dispatch(getMemberConfig()).then(unwrapResult)
      .catch((error) => {
        logger.error(Logger.format('Request failed:', error));
      });

    dispatch(getPaymentMethods());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Block only while there is truly no member/company data yet (first-ever
  // load, or right after logout clears persisted state) - not on every
  // refresh, where redux-persist already has a (possibly slightly stale)
  // copy to render immediately while the fetch above refreshes it in the
  // background. This lets every dashboard page (and anything reading
  // subscription/company data, e.g. useHasAccess) assume company data is
  // present by the time it renders, without flashing a loader on every visit.
  if (getMemberInformationStatus === 'pending' && !company) {
    return (
      <Container sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <CircularProgress color="inherit" size={40} />
      </Container>
    );
  }

  return (
    <AuthenticatedRoute>
      <>
        <DashboardLayout>
          {outlet || <Navigate to={dashboardEntryRoute} />}
        </DashboardLayout>
      </>
    </AuthenticatedRoute>
  );
}
